/**
 * <expandable-text> — "read more / read less" that expands in place.
 *
 * Progressive enhancement: without JS the full text is shown (the clip has no
 * inline height and the toggle button stays hidden). On connect this measures
 * the content, and — only if it actually overflows the collapsed height — clips
 * it and reveals the toggle. Height is animated via an explicit `block-size`
 * (measured in px) so the transition is smooth in every browser, unlike the
 * pure-CSS `grid-template-rows` trick which only interpolates fr↔fr.
 */
class ExpandableText extends HTMLElement {
  connectedCallback() {
    this.clip = this.querySelector('[data-clip]');
    this.content = this.querySelector('.pdp-description-inline__content');
    this.button = this.querySelector('[data-toggle]');
    if (!this.clip || !this.content || !this.button) return;

    this.collapsedHeight = this.getCollapsedHeight();

    // Nothing to collapse — leave the full text visible, button hidden.
    if (this.content.scrollHeight <= this.collapsedHeight + 1) return;

    this.setAttribute('data-enhanced', '');
    // Set the collapsed height without animating the initial paint.
    this.clip.style.blockSize = `${this.collapsedHeight}px`;
    this.button.hidden = false;

    this.button.addEventListener('click', () => this.toggle());
    this.clip.addEventListener('transitionend', (event) => this.onTransitionEnd(event));
  }

  get expanded() {
    return this.hasAttribute('data-expanded');
  }

  getCollapsedHeight() {
    const lines = parseInt(this.style.getPropertyValue('--pdp-desc-lines'), 10) || 3;
    const styles = getComputedStyle(this.content);
    let lineHeight = parseFloat(styles.lineHeight);
    if (Number.isNaN(lineHeight)) {
      // `line-height: normal` — approximate from the font size.
      lineHeight = parseFloat(styles.fontSize) * 1.5;
    }
    return Math.round(lines * lineHeight);
  }

  toggle() {
    if (this.expanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  expand() {
    this.setAttribute('data-expanded', '');
    this.button.setAttribute('aria-expanded', 'true');
    this.clip.style.blockSize = `${this.content.scrollHeight}px`;
  }

  collapse() {
    // Start from a concrete height (the clip may currently be `auto`) so the
    // transition to the collapsed height has something to interpolate from.
    this.clip.style.blockSize = `${this.content.scrollHeight}px`;
    // Force a reflow so the browser registers the start height.
    void this.clip.offsetHeight;

    this.removeAttribute('data-expanded');
    this.button.setAttribute('aria-expanded', 'false');
    this.clip.style.blockSize = `${this.collapsedHeight}px`;
  }

  onTransitionEnd(event) {
    if (event.propertyName !== 'block-size' && event.propertyName !== 'height') return;
    // Once fully expanded, drop the fixed height so the block stays responsive
    // (e.g. on viewport resize / reflow).
    if (this.expanded) {
      this.clip.style.blockSize = 'auto';
    }
  }
}

if (!customElements.get('expandable-text')) {
  customElements.define('expandable-text', ExpandableText);
}
